import { Camera, ImagePlus, RotateCcw, UploadCloud } from "lucide-react";
import { useRef, useState } from "react";
import { uploadPhotos } from "../api";

const categories = [
  "Container Exterior",
  "Open Container",
  "Contents",
  "Serial Number",
  "Damage",
  "Packing Configuration",
  "Loading Position",
  "Destination Placement",
  "Receipt",
  "Documentation",
  "Other"
];

export function PhotoUploader({
  unitId,
  onComplete
}: {
  unitId: string;
  onComplete: () => void;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState("Contents");
  const [progress, setProgress] = useState(0);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [lastFiles, setLastFiles] = useState<File[]>([]);

  async function send(files: FileList | File[]) {
    const list = Array.from(files);
    if (!list.length) return;
    setLastFiles(list);
    setWorking(true);
    setError("");
    setProgress(0);
    try {
      await uploadPhotos(unitId, list, category, setProgress);
      onComplete();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Upload failed");
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="photo-uploader">
      <label>
        Photo category
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          {categories.map((value) => (
            <option key={value}>{value}</option>
          ))}
        </select>
      </label>
      <div className="upload-actions">
        <button className="button primary" type="button" onClick={() => camera.current?.click()}>
          <Camera size={20} /> Take photo
        </button>
        <button className="button secondary" type="button" onClick={() => library.current?.click()}>
          <ImagePlus size={20} /> Choose photos
        </button>
      </div>
      <input
        ref={camera}
        className="visually-hidden"
        type="file"
        accept="image/heic,image/heif,image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={(event) => event.target.files && send(event.target.files)}
      />
      <input
        ref={library}
        className="visually-hidden"
        type="file"
        accept="image/heic,image/heif,image/jpeg,image/png,image/webp"
        multiple
        onChange={(event) => event.target.files && send(event.target.files)}
      />
      {working && (
        <div className="upload-progress">
          <UploadCloud size={18} />
          <div>
            <span>Uploading {lastFiles.length} photo{lastFiles.length === 1 ? "" : "s"}…</span>
            <progress value={progress} max="100" />
          </div>
          <strong>{progress}%</strong>
        </div>
      )}
      {error && (
        <div className="upload-error">
          <span>{error}</span>
          <button className="button secondary" type="button" onClick={() => send(lastFiles)}>
            <RotateCcw size={18} /> Retry
          </button>
        </div>
      )}
    </div>
  );
}

